import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Loader2, EyeOff, Eye, AlertTriangle, ArrowLeft, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { StarRow } from "@/components/StoreRating";

/**
 * ParentReviews — moderation feed for the current parent's kids' storefronts.
 * Reads from GET /api/parent/reviews and lets parents hide/unhide individual
 * reviews via POST /api/parent/reviews/{id}/hide.
 *
 * Route: /dashboard/reviews (parent-authed).
 */
export default function ParentReviews() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/parent/reviews");
      setRows(data?.reviews || []);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't load your kids' reviews.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 4500); };

  const toggleHide = async (row) => {
    setBusyId(row.id);
    const targetHidden = !row.hidden;
    try {
      await api.post(`/parent/reviews/${row.id}/hide`, { hidden: targetHidden });
      setRows((r) => r.map((x) => x.id === row.id ? { ...x, hidden: targetHidden } : x));
      showFlash(targetHidden
        ? "Review hidden from the public shop."
        : "Review restored — buyers can see it again.");
    } catch (e) {
      showFlash(formatApiError(e) || "Couldn't update this review. Try again.");
    } finally { setBusyId(""); }
  };

  const visible = rows.filter((r) => !r.hidden);
  const hidden = rows.filter((r) => r.hidden);

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="parent-reviews-page">
      <Navbar />
      <section className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/dashboard/parent" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-brandTeal transition mb-4" data-testid="parent-reviews-back">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>
        <div className="pill bg-amber-100 text-amber-800 inline-flex"><MessageCircle className="w-3 h-3 mr-1" /> Store reviews</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate mt-3">
          Reviews on your kids&rsquo; shops
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Buyers leave a star rating + short note after their order. If anything ever looks unfair or off, tap Hide and it disappears from the public shop straight away.
        </p>

        {flash && (
          <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-900" data-testid="parent-reviews-flash" role="status">
            {flash}
          </div>
        )}

        {loading ? (
          <div className="card-base p-8 mt-6 text-center text-sm text-slate-500 inline-flex items-center gap-2" data-testid="parent-reviews-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading&hellip;
          </div>
        ) : err ? (
          <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800 inline-flex items-center gap-2" data-testid="parent-reviews-error">
            <AlertTriangle className="w-4 h-4" /> {err}
          </div>
        ) : rows.length === 0 ? (
          <div className="card-base p-10 mt-6 text-center" data-testid="parent-reviews-empty">
            <Star className="w-8 h-8 text-brandTeal mx-auto fill-brandTeal/30" data-testid="parent-reviews-empty-icon" />
            <div className="font-bold text-brandSlate mt-3">No reviews yet</div>
            <p className="text-sm text-slate-500 mt-1">The moment a buyer leaves feedback on one of your kids&rsquo; toys, it will show up here.</p>
          </div>
        ) : (
          <>
            {visible.length > 0 && (
              <div className="mt-8">
                <h2 className="font-display text-xl font-semibold text-brandSlate mb-3">Live on the shop &middot; {visible.length}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {visible.map((r) => (
                    <ReviewCard key={r.id} r={r} busy={busyId === r.id} onToggle={toggleHide} testIdPrefix="parent-review-visible" />
                  ))}
                </div>
              </div>
            )}
            {hidden.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-xl font-semibold text-brandSlate mb-3 flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-slate-400" /> Hidden &middot; {hidden.length}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {hidden.map((r) => (
                    <ReviewCard key={r.id} r={r} busy={busyId === r.id} onToggle={toggleHide} testIdPrefix="parent-review-hidden" dimmed />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

function ReviewCard({ r, busy, onToggle, testIdPrefix, dimmed = false }) {
  return (
    <div
      className={`card-base p-5 ${dimmed ? "opacity-60" : ""}`}
      data-testid={`${testIdPrefix}-${r.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-brandSlate truncate">{r.reviewer_name || "A buyer"}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }) : ""}
          </div>
        </div>
        <StarRow value={r.rating || 0} size={16} />
      </div>
      {r.note && (
        <p className="text-sm text-slate-700 mt-2 leading-relaxed break-words">
          &ldquo;{r.note}&rdquo;
        </p>
      )}
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => onToggle(r)}
          disabled={busy}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition disabled:opacity-60 ${
            r.hidden
              ? "bg-brandTeal hover:bg-brandTealDark text-white"
              : "bg-white border border-slate-200 hover:border-rose-300 text-slate-700 hover:text-rose-700"
          }`}
          data-testid={`${testIdPrefix}-toggle-${r.id}`}
        >
          {busy
            ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving&hellip;</>)
            : r.hidden
              ? (<><Eye className="w-3.5 h-3.5" /> Restore</>)
              : (<><EyeOff className="w-3.5 h-3.5" /> Hide from shop</>)}
        </button>
      </div>
    </div>
  );
}
